/**
 * IncidentNotificationDrafterService — AI-drafted incident notification
 * text with human approval (V3-5-3).
 *
 * The drafter produces a regulatory notification draft from the incident
 * fields + computed obligations. The draft is NOT sent automatically —
 * it requires explicit human approval before dispatch. Provider
 * provenance (provider_id, model_id) is stamped on every draft so the
 * verification affordance can show what generated the text.
 */
import { defineTemplate, field } from '../egress/data-classes.js';
import { computeObligations, } from './incident-service.js';
/**
 * ADR-0073: the notification prompt as reviewed templates. Regulator and statute
 * come from the obligation rules (reference); every incident field is tenant
 * (owner decision 2026-10-08: incident descriptions default to tenant; a
 * workspace raises them to federal).
 */
export const INCIDENT_PROMPT = [
    'Draft a regulatory incident notification for the following:',
    'Regulator: {regulator}',
    'Statute: {statute}',
    'Deadline: {deadline}',
    'Incident: {title}',
    'Severity: {severity}',
    'Jurisdiction: {jurisdiction}',
    'Data classes: {data_classes}',
    'Affected persons: {affected}',
    'Reported at: {reported_at}',
    'Organization: {organization}',
    '',
    'Requirements:',
    '- Reference ONLY the incident fields above; do not invent facts.',
    '- State the statutory basis and deadline.',
    '- Keep the draft under 500 words.',
].join('\n');
export const INCIDENT_INPUT_TEMPLATE = defineTemplate('incident.notification_draft.input', 'v1', INCIDENT_PROMPT, ['regulator', 'statute', 'deadline', 'title', 'severity', 'jurisdiction', 'data_classes', 'affected', 'reported_at', 'organization']);
export const INCIDENT_TEMPLATE = defineTemplate('incident.notification_draft', 'v1', 'Draft a regulatory incident notification. Return JSON with subject and body fields.', []);
function promptSlots(input, obligation) {
    const t = (v) => field('incident.description', v);
    return {
        regulator: field('rules.rule_text', obligation.regulator),
        statute: field('rules.rule_text', obligation.statute_ref),
        deadline: t(obligation.deadline_at),
        title: t(input.title),
        severity: t(input.severity),
        jurisdiction: t(input.jurisdiction),
        data_classes: t(input.data_classes.join(', ')),
        affected: t(String(input.affected_persons_count ?? 'unknown')),
        reported_at: t(input.reported_at),
        organization: t(input.workspace_name),
    };
}
function buildFallbackDraft(input, obligation) {
    return {
        subject: `[${input.severity.toUpperCase()}] Incident notification — ${obligation.regulator} (${obligation.statute_ref})`,
        body: [
            `To: ${obligation.regulator}`,
            `Re: ${obligation.statute_ref}`,
            ``,
            `${input.workspace_name} hereby notifies of an incident:`,
            `- Title: ${input.title}`,
            `- Severity: ${input.severity}`,
            `- Jurisdiction: ${input.jurisdiction}`,
            `- Data classes involved: ${input.data_classes.join(', ')}`,
            `- Affected persons: ${input.affected_persons_count ?? 'To be determined'}`,
            `- Reported at: ${input.reported_at}`,
            ``,
            `Notification deadline: ${obligation.deadline_at}`,
            ``,
            `[THIS IS A DRAFT — REQUIRES HUMAN REVIEW AND APPROVAL BEFORE SUBMISSION]`,
        ].join('\n'),
    };
}
export async function draftNotifications(input, llm) {
    const obligations = computeObligations({
        jurisdiction: input.jurisdiction,
        data_classes: input.data_classes,
        severity: input.severity,
        affected_persons_count: input.affected_persons_count,
        outcome: input.outcome,
        incident_discovered_at: input.reported_at,
    });
    const drafts = [];
    for (const ob of obligations) {
        let subject = '';
        let body = '';
        let providerId = null;
        let modelId = null;
        const fallback = () => { const f = buildFallbackDraft(input, ob); subject = f.subject; body = f.body; };
        if (llm) {
            try {
                const result = await llm.run({
                    task: 'incident.notification_draft', template: INCIDENT_TEMPLATE, inputTemplate: INCIDENT_INPUT_TEMPLATE,
                    slots: promptSlots(input, ob),
                    schemaHint: { type: 'object', properties: { subject: { type: 'string' }, body: { type: 'string' } } },
                });
                if (result.status === 'ok') {
                    subject = result.output.subject;
                    body = result.output.body;
                    providerId = result.provider_id;
                    modelId = result.model_id ?? null;
                }
                else {
                    // Refused (federal mode / raised class): parity incident_notification_drafting → deterministic.
                    fallback();
                }
            }
            catch {
                fallback();
            }
        }
        else {
            fallback();
        }
        drafts.push({
            incident_id: input.incident_id,
            regulator: ob.regulator,
            statute_ref: ob.statute_ref,
            deadline_at: ob.deadline_at,
            subject,
            body,
            provider_id: providerId,
            model_id: modelId,
            approved: false,
        });
    }
    return { drafts, obligations };
}
//# sourceMappingURL=incident-notification-drafter-service.js.map